import type { RngState, Seat } from "@bing/engine-core";

/* ------------------------------------------------------------------ *
 * مار پله — Snakes and Ladders.
 *
 * THE SHARED CONTRACT (card-86 planner). Every batch builds on these
 * names: the engine (batch A) produces them, the game-server ships the
 * view over the socket, the web (batches B1/B2) draws them. Change a
 * field here only together with every reader — a string matched across
 * the wire is a contract between two IMAGES.
 *
 * Design: docs/superpowers/specs/2026-11-03-snakes-ladders-design.md
 * ------------------------------------------------------------------ */

/** The game id — `GAMES` in @bing/shared, Prisma `LADDERS`. */
export const LADDERS_GAME_ID = "ladders";

/** Cells on the board, numbered 1..100. */
export const FINAL_CELL = 100;
/** The board is 10 × 10, numbered boustrophedon from the bottom-left (see `cellCoord`). */
export const BOARD_COLS = 10;
export const BOARD_ROWS = 10;
/** A piece that has not entered the board yet stands on 0 — before cell 1. No 6 is needed to enter. */
export const START_CELL = 0;
/** One ordinary die. */
export const DIE_FACES = 6;
/**
 * EVERY 6 EARNS ONE MORE ROLL — THE OPERATOR'S RULE (2026-11-03): «As long as the user rolls a 6,
 * they get a bonus to roll the dice one more time.» After a plain move, a ladder, a snake, AND a 6
 * that cannot move because it would pass 100. No cap on consecutive 6s. The only 6 that earns
 * nothing is the one that reaches 100 — the game is over.
 */
export const BONUS_ROLL = 6;

/** The most seats a table can carry. */
export const LADDERS_SEATS = 4;
/** The table sizes the platform OFFERS (lobby, private host, solo). The engine itself plays 2..4. */
export const LADDERS_PLAYER_COUNTS = [2, 4] as const;
export type LaddersPlayerCount = (typeof LADDERS_PLAYER_COUNTS)[number];

/**
 * Bump when a rule changes in a way an OLD saved solo game cannot survive; the engine's
 * `canResume` refuses any other value (see `GameEngine.canResume` — and its deploy-order note).
 * 2 = ROUND 2 (card-86, 2026-11-03): the two-phase turn (roll, then move), capture, and the
 * level/style carried in the state — a v1 save has none of them, so it is retired, never guessed.
 */
export const LADDERS_RULES_VERSION = 2;

/* ---- ROUND 2: LEVELS AND LOOKS (the operator, 2026-11-03) ----------------------------------
 * «Levels: آسان, متوسط and سخت, each with enough different maps … Public tables are always
 * متوسط, with a random map; solo and private tables pick the level.» and «Looks, three styles:
 * the current jungle background; a second picture background …; and «اورجینال», a classic board
 * with no picture. Solo and private tables choose the style; public tables get one of the two
 * picture backgrounds at random.»
 *
 * ⚠️ MIRRORED IN @bing/shared (`LADDERS_LEVEL_IDS` / `LADDERS_STYLE_IDS` in dto/table.ts — the
 * shared package depends on zod only, never on an engine). `apps/game-server/test/
 * ladders-contract.test.ts` pins the two copies equal, order included.
 * ------------------------------------------------------------------------------------------ */

/** The three levels, easiest first. A level is a SET OF MAPS (`LADDERS_BOARDS_BY_LEVEL`). */
export const LADDERS_LEVELS = ["easy", "medium", "hard"] as const;
export type LaddersLevel = (typeof LADDERS_LEVELS)[number];
/** A private table's and a solo game's level when none is chosen. */
export const DEFAULT_LADDERS_LEVEL: LaddersLevel = "medium";
/** A PUBLIC table's level — always (the operator: «Public tables are always متوسط»). */
export const PUBLIC_LADDERS_LEVEL: LaddersLevel = "medium";

/**
 * The three looks. `jungle` = the round-1 picture; `bg2` = the operator's second picture
 * (`/root/card/art-inbox/snakes-ladders-bg2.jpg`); `classic` = «اورجینال», a classic board with
 * no picture. A look changes NOTHING about play — only what the web draws.
 */
export const LADDERS_STYLES = ["jungle", "bg2", "classic"] as const;
export type LaddersStyle = (typeof LADDERS_STYLES)[number];
/** The looks drawn over a PICTURE — the pool a public table's random look is drawn from. */
export const LADDERS_PICTURE_STYLES: readonly LaddersStyle[] = ["jungle", "bg2"];
/** A private table's and a solo game's look when none is chosen. */
export const DEFAULT_LADDERS_STYLE: LaddersStyle = "jungle";
/**
 * THE LOOKS THIS BUILD CAN DRAW — what the pickers offer, what the server accepts, and (crossed
 * with {@link LADDERS_PICTURE_STYLES}) what a public table draws from. `bg2` joined this list in
 * the commit that made its picture and its measurements live (card-86 round 2's integration: the
 * operator's file, prepared by batch P) — so a public table draws the jungle or bg2. Append-only;
 * the gs and the web ship from one tree, so they agree.
 */
export const LADDERS_LIVE_STYLES: readonly LaddersStyle[] = ["jungle", "classic", "bg2"];

/** The picture looks a PUBLIC table draws from, uniformly: live ∩ picture (the jungle and bg2). */
export function laddersPublicStyles(): readonly LaddersStyle[] {
  return LADDERS_LIVE_STYLES.filter((s) => LADDERS_PICTURE_STYLES.includes(s));
}

/** `raw` if it names a level, else undefined — never a throw (rules arrive from a client). */
export function laddersLevelOf(raw: unknown): LaddersLevel | undefined {
  return typeof raw === "string" && (LADDERS_LEVELS as readonly string[]).includes(raw)
    ? (raw as LaddersLevel)
    : undefined;
}

/** `raw` if it names a look THIS BUILD can draw ({@link LADDERS_LIVE_STYLES}), else undefined. */
export function laddersLiveStyleOf(raw: unknown): LaddersStyle | undefined {
  return typeof raw === "string" && (LADDERS_LIVE_STYLES as readonly string[]).includes(raw)
    ? (raw as LaddersStyle)
    : undefined;
}

/** A snake: landing on its HEAD sends the piece down to its TAIL (`head > tail`). */
export interface Snake {
  readonly head: number;
  readonly tail: number;
}

/** A ladder: landing on its FOOT takes the piece up to its TOP (`top > foot`). */
export interface Ladder {
  readonly foot: number;
  readonly top: number;
}

/**
 * One board layout. Pure data: WHERE the snakes and ladders are — never how they look.
 * The web picks each one's look from its index (snake i → skin i, ladder i → material i),
 * so the ORDER of these arrays is part of a board's identity: never reorder a shipped board.
 *
 * Every shipped board passes `boardProblems` (./board): nothing on 1 or 100, every endpoint a
 * different cell (so no chains, and no cell is both a start and an end), every snake and ladder
 * spans at least one row, no two ladders cross, and 100 is reachable from every reachable cell.
 */
export interface LaddersBoard {
  /** Stable, unique, never reused: `"b1"`… */
  readonly id: string;
  readonly snakes: readonly Snake[];
  readonly ladders: readonly Ladder[];
}

/**
 * THE TWO-PHASE TURN (round 2 — the operator: «they tap to roll the die, then tap their piece to
 * move it»). The seat on turn first ROLLS (the SERVER throws from `rngState`, so a client can never
 * choose its number), then MOVES its one piece by that die. There is nothing to choose in a move
 * — one piece a player — so `move` carries no payload: it is the tap.
 *  - roll phase: the only legal intent is `roll`;
 *  - move phase: the only legal intent is `move` (the die is `pendingDie`);
 *  - an OVERSHOOT (the die would pass 100) has NO move phase: the throw resolves at once, the piece
 *    stays, and the turn passes — or, on a 6, the same seat rolls again;
 *  - a 6 chains only AFTER its move completes (never before the piece has moved).
 */
export type LaddersMove = { readonly kind: "roll" } | { readonly kind: "move" };

/** What the seat on turn does next. */
export type LaddersPhase = "roll" | "move";

/** Rule options. */
export interface LaddersConfig {
  /** Seats actually playing (2..4). Seats are always `0..playerCount-1`. */
  readonly playerCount: number;
  /** The level the board was drawn from ({@link LADDERS_BOARDS_BY_LEVEL}). */
  readonly level: LaddersLevel;
  /** The look every seat draws — the server's, so every screen agrees (a public table's is random). */
  readonly style: LaddersStyle;
}

/** A snake or a ladder taken at the end of a move. */
export interface LaddersJump {
  readonly kind: "ladder" | "snake";
  /** Index into `board.ladders` / `board.snakes` — the web draws THAT one's climb or slide. */
  readonly index: number;
  /** The foot / head the move landed on. */
  readonly from: number;
  /** The top / tail it ends on. */
  readonly to: number;
}

/**
 * CAPTURE (round 2 — the operator: «when a piece stops on a cell where another player's piece
 * already stands — after any snake or ladder — that piece is hit and goes back to the start. Not on
 * 100.»). Judged on the mover's FINAL cell (`to`, after any snake or ladder), never on a cell walked
 * past or on `landed`. EVERY opponent piece there goes back to {@link START_CELL} (0, the off-board
 * start: no 6 is needed to enter again). 100 is exempt (the game is over). No extra roll and no
 * other side effect.
 */
export interface LaddersCapture {
  /** The seat whose piece was hit. */
  readonly seat: Seat;
  /** The cell it was hit on (= the mover's `to`); it is now on {@link START_CELL}. */
  readonly cell: number;
}

/**
 * One THROW of the die (the roll phase). The client throws the die for every new `rollCount`.
 * A throw that can move opens the move phase (`pendingDie`); an OVERSHOOT resolves here and now.
 */
export interface LaddersRollEvent {
  /** The seat that threw. */
  readonly seat: Seat;
  /** The value, 1..6. */
  readonly die: number;
  /** Where that seat's piece stood (0 = off the board). */
  readonly from: number;
  /**
   * The throw would pass 100: NO move phase — the piece stays (no bounce anywhere, the operator's
   * rule), «باید دقیقاً N بیاری» may be read, and the turn passes or, on a 6, rolls again.
   */
  readonly overshoot: boolean;
  /**
   * An overshoot 6: the SAME seat rolls again (every 6 earns one more roll, an overshoot 6
   * included). Always false for a throw that can move — that throw's extra roll is its MOVE's
   * `again`, earned only once the move completes.
   */
  readonly again: boolean;
}

/**
 * One MOVE (the move phase), in the order the client animates it: the walk along `path` (one cell
 * per pip) → the `jump`, if any → the hit pieces sliding home (`captured`). The client keys it on
 * `moveCount`, never on the event's identity.
 */
export interface LaddersMoveEvent {
  /** The seat that moved. */
  readonly seat: Seat;
  /** The die it moved by (the throw before it), 1..6. */
  readonly die: number;
  /** Where the piece stood (0 = off the board). */
  readonly from: number;
  /** Every cell stepped on, in order, ONE PER PIP — `path.length === die`, never empty. */
  readonly path: readonly number[];
  /** The cell the walk ended on (`from + die`), BEFORE any snake or ladder. */
  readonly landed: number;
  /** The snake or ladder whose start is `landed`, else null. Never two: endpoints are distinct. */
  readonly jump: LaddersJump | null;
  /** The final resting cell: `jump.to` when there is a jump, else `landed`. */
  readonly to: number;
  /** The opponents hit on `to` (empty on 100, and wherever nobody stood), in seat order. */
  readonly captured: readonly LaddersCapture[];
  /** A 6 that did not win: the SAME seat rolls again (the operator's rule, {@link BONUS_ROLL}). */
  readonly again: boolean;
  /** The move ended exactly on 100. */
  readonly won: boolean;
}

/** The authoritative state. Pure data — no clocks, no I/O. */
export interface LaddersState {
  /** The layout this game plays — carried whole, so a saved game resumes on its own board. */
  readonly board: LaddersBoard;
  /** `positions[seat]` = that seat's cell, 0..100. Length = `config.playerCount`. */
  readonly positions: readonly number[];
  /** The seat on turn — to roll, or to move by `pendingDie`. */
  readonly turn: Seat;
  /** The thrown die waiting for `turn`'s move — non-null EXACTLY in the move phase. */
  readonly pendingDie: number | null;
  /**
   * MONOTONIC count of every THROW (overshoots included). The client throws the die whenever it
   * changes — never on an event's identity (every frame is fresh JSON).
   */
  readonly rollCount: number;
  /** MONOTONIC count of every MOVE. The client animates the walk/jump/capture whenever it changes. */
  readonly moveCount: number;
  readonly rngState: RngState;
  readonly winner: Seat | null;
  readonly config: LaddersConfig;
  /** The last throw; null before the first. */
  readonly lastRoll: LaddersRollEvent | null;
  /** The last move; null before the first. */
  readonly lastMove: LaddersMoveEvent | null;
  /** What the room last applied — `postMoveDelayMs` paces a bot by it (منچ's `lastAction`). */
  readonly lastAction: "roll" | "move" | null;
  /** {@link LADDERS_RULES_VERSION} at creation. */
  readonly rulesVersion: number;
}

/**
 * The per-seat projection. Perfect information — nothing is redacted; only `you`, `canRoll` and
 * `canMove` differ. THE GAME-START FRAME (the first `state`) already carries `board` (its `id`
 * names the map), `level` and `style`, so every seat draws the same look from its first paint.
 */
export interface LaddersView {
  readonly you: Seat;
  readonly turn: Seat;
  readonly board: LaddersBoard;
  /** The level the map was drawn from. */
  readonly level: LaddersLevel;
  /**
   * The look to draw. ⚠️ A web bundle that does not know a value (a newer gs) draws
   * {@link DEFAULT_LADDERS_STYLE} — never a blank scene.
   */
  readonly style: LaddersStyle;
  readonly positions: readonly number[];
  readonly playerCount: number;
  /** What the seat on turn does next (meaningless once `status` is "over"). */
  readonly phase: LaddersPhase;
  /** Your turn, roll phase, game still playing. */
  readonly canRoll: boolean;
  /** Your turn, move phase, game still playing — your piece is the tap target (highlighted). */
  readonly canMove: boolean;
  /**
   * The die showing: the pending throw in the move phase, else the last throw's value; null
   * before the first throw.
   */
  readonly die: number | null;
  /** Who threw the die showing, null before the first throw. */
  readonly roller: Seat | null;
  /** {@link LaddersState.rollCount} — throw the die whenever this changes. */
  readonly rollCount: number;
  /** {@link LaddersState.moveCount} — animate the move whenever this changes. */
  readonly moveCount: number;
  readonly lastRoll: LaddersRollEvent | null;
  readonly lastMove: LaddersMoveEvent | null;
  readonly status: "playing" | "over";
  readonly winner?: Seat;
}
